import { useEffect, useMemo, useRef, useState } from 'react'

import complianceJson from '../../demo/fixtures/w2/compliance.json'
import { useDemoStore } from '../../demo/store'
import type { Violation } from '../../demo/types'
import { Badge, Button, EmptyState, GlossaryText, Icon, Modal, Spinner } from '../../ui'
import { COMPLIANCE_COPY, violationDialogTitle } from '../monitor/compliance'
import { CreativeSwapPicker } from './CreativeSwapPicker'
import styles from './ComplianceAlerts.module.css'

type ProcessingState = 'reviewing' | 'appealing' | 'replaced' | 'appealed'

interface ViolationDialogProps {
  open: boolean
  campaignId: string
  onClose: () => void
}

function assetUrl(path: string): string {
  if (!path || path.startsWith('/') || path.startsWith('data:')) return path
  return `/${path}`
}

export function ViolationDialog({ open, campaignId, onClose }: ViolationDialogProps) {
  const allViolations = useDemoStore((state) => state.violations)
  const ads = useDemoStore((state) => state.ads)
  const creatives = useDemoStore((state) => state.creatives)
  const replaceAdCreative = useDemoStore((state) => state.replaceAdCreative)
  const appealViolation = useDemoStore((state) => state.appealViolation)
  const resolveViolation = useDemoStore((state) => state.resolveViolation)
  const activeIds = useMemo(() => allViolations.filter((item) => item.campaignId === campaignId && item.state !== 'resolved').map((item) => item.id), [allViolations, campaignId])
  const [displayIds, setDisplayIds] = useState<string[]>([])
  const [processing, setProcessing] = useState<Record<string, ProcessingState>>({})
  const [pickerFor, setPickerFor] = useState<string | null>(null)
  const wasOpen = useRef(false)
  const [timers] = useState(() => new Set<number>())

  useEffect(() => {
    if (open && !wasOpen.current) {
      setDisplayIds(activeIds)
      setProcessing({})
      setPickerFor(null)
    }
    wasOpen.current = open
  }, [activeIds, open])

  useEffect(() => () => {
    timers.forEach((timer) => window.clearTimeout(timer))
    timers.clear()
  }, [timers])

  useEffect(() => {
    if (!open || displayIds.length > 0) return
    const timer = window.setTimeout(onClose, COMPLIANCE_COPY.autoCloseMs)
    return () => window.clearTimeout(timer)
  }, [displayIds.length, onClose, open])

  const later = (callback: () => void, delay: number) => {
    const timer = window.setTimeout(() => {
      timers.delete(timer)
      callback()
    }, delay)
    timers.add(timer)
  }

  const finish = (violation: Violation, result: ProcessingState, delay: number) => {
    later(() => {
      resolveViolation(violation.id)
      setProcessing((current) => ({ ...current, [violation.id]: result }))
      later(() => setDisplayIds((current) => current.filter((id) => id !== violation.id)), 600)
    }, delay)
  }

  const replace = (violation: Violation, creativeId: string) => {
    replaceAdCreative(violation.adId, creativeId)
    setPickerFor(null)
    setProcessing((current) => ({ ...current, [violation.id]: 'reviewing' }))
    finish(violation, 'replaced', complianceJson.actions.replaceCreative.reviewMs)
  }

  const appeal = (violation: Violation) => {
    appealViolation(violation.id)
    setProcessing((current) => ({ ...current, [violation.id]: 'appealing' }))
    finish(violation, 'appealed', complianceJson.actions.appeal.pendingMs)
  }

  const displayed = displayIds
    .map((id) => allViolations.find((violation) => violation.id === id))
    .filter((violation): violation is Violation => Boolean(violation))
  const single = displayed.length === 1 ? displayed[0] : undefined

  const actionButtons = (violation: Violation) => {
    const state = processing[violation.id]
    const busy = state === 'reviewing' || state === 'appealing'
    if (state === 'replaced' || state === 'appealed') {
      return <span className={styles.success}><Icon name="check-circle" size={18} />{state === 'appealed' ? complianceJson.actions.appeal.then : complianceJson.actions.replaceCreative.then}</span>
    }
    return (
      <>
        <Button variant="secondary" disabled={busy} onClick={() => setPickerFor(violation.id)}>
          <Icon name="vault" size={17} />{complianceJson.actions.replaceCreative.label}
        </Button>
        <Button disabled={busy} onClick={() => appeal(violation)}>{complianceJson.actions.appeal.label}</Button>
        {busy ? <span className={styles.busy}><Spinner />{state === 'appealing' ? '申诉中…' : '审核中'}</span> : null}
      </>
    )
  }

  return (
    <Modal
      open={open}
      title={<span className={styles.dialogTitle}>{violationDialogTitle(displayed.length)}</span>}
      onClose={onClose}
      footer={displayed.length === 0 ? undefined : single ? actionButtons(single) : <Button variant="secondary" onClick={onClose}>关闭</Button>}
    >
      <div data-scope="state-dialog-violation" className={styles.dialogContent}>
        {displayed.length === 0 ? (
          <EmptyState className={styles.compactEmpty} icon="check-circle" title={COMPLIANCE_COPY.violationEmpty} text="违规计数与投放状态已同步更新" />
        ) : (
          <>
            <div className={styles.violationList}>
              {displayed.map((violation) => {
                const ad = ads.find((item) => item.id === violation.adId)
                const creative = creatives.find((item) => item.id === ad?.creativeId)
                return (
                  <div className={`${styles.violationItem} ${displayed.length > 1 ? styles.violationItemMulti : ''}`} key={violation.id}>
                    <img className={styles.violationThumb} src={creative?.dataUrl || assetUrl(creative?.cover ?? '')} alt={creative?.name ?? ad?.name ?? ''} />
                    <div className={styles.violationText}>
                      <p>{ad?.name ?? '广告'}</p>
                      <p className={styles.violationSub}><GlossaryText text={`${violation.reason} · ${violation.effect}`} /></p>
                    </div>
                    {processing[violation.id] === 'reviewing' ? <Badge tone="orange">审核中</Badge> : null}
                    {processing[violation.id] === 'appealing' ? <Badge tone="orange">申诉中…</Badge> : null}
                    {processing[violation.id] === 'replaced' || processing[violation.id] === 'appealed' ? <Badge tone="green">已恢复投放</Badge> : null}
                    {displayed.length > 1 ? <div className={styles.inlineActions}>{actionButtons(violation)}</div> : null}
                  </div>
                )
              })}
            </div>
            <p className={styles.dialogHint}>{COMPLIANCE_COPY.violationDialogHint}</p>
            {pickerFor ? (() => {
              const violation = displayed.find((item) => item.id === pickerFor)
              const ad = ads.find((item) => item.id === violation?.adId)
              return violation && ad ? (
                <CreativeSwapPicker campaignId={campaignId} currentCreativeId={ad.creativeId} onCancel={() => setPickerFor(null)} onSelect={(creativeId) => replace(violation, creativeId)} />
              ) : null
            })() : null}
          </>
        )}
      </div>
    </Modal>
  )
}
